import { useQueryUI } from '@/sections/insights/queryBuilder/state/QueryUIContext';
import { useQueryData } from '@/sections/insights/queryBuilder/state/useQueryData';
import type { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';

/**
 * Convenience hook that combines both data and UI contexts
 *
 * Use this in components that need both contexts.
 *
 * For components that only need one context, use:
 * - useQueryData() for data/mutations only (e.g., QueryVisualizer)
 * - useQueryUI() for UI state only (e.g., Sidebar)
 *
 * Performance note: This hook causes re-renders when EITHER context changes.
 * If your component only needs one context, use the specific hook to avoid unnecessary re-renders.
 */
export function useQueryBuilder() {
  const dataContext = useQueryData();
  const uiContext = useQueryUI();

  // Convenience wrapper for SET_QUERY action
  const setQuery = (query: SavedQuery) => {
    dataContext.dispatch({ type: 'SET_QUERY', query });
  };

  return {
    // Data context
    queryState: dataContext.queryState,
    dispatch: dataContext.dispatch,
    getSavedQuery: dataContext.getSavedQuery,
    setQuery,

    // UI context
    mode: uiContext.mode,
    previousMode: uiContext.previousMode,
    isMaximized: uiContext.isMaximized,
    sidebarCollapsed: uiContext.sidebarCollapsed,
    isGlobal: uiContext.isGlobal,
    usernames: uiContext.usernames,
    graphLayout: uiContext.graphLayout,
    setMode: uiContext.setMode,
    restorePreviousMode: uiContext.restorePreviousMode,
    setSidebarCollapsed: uiContext.setSidebarCollapsed,
    toggleMaximized: uiContext.toggleMaximized,
    setIsGlobal: uiContext.setIsGlobal,
    setUsernames: uiContext.setUsernames,
    setGraphLayout: uiContext.setGraphLayout,
  };
}

// Export providers for root component
export { QueryDataProvider } from './QueryDataProvider';
export { QueryUIProvider } from './QueryUIContext';
